/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import { defineMessages } from 'react-intl';

export const TextToSpeech = defineMessages({
  title: {
    id: 'phone.TextToSpeech.title',
    defaultMessage: 'Text to Speech'
  },
  configureVoiceTitle: {
    id: 'phone.TextToSpeech.configureVoiceTitle',
    defaultMessage: 'Configure voice'
  },
  configureVoiceDescription: {
    id: 'phone.TextToSpeech.configureVoiceDescription',
    defaultMessage:
      'This is the voice your assistant uses when speaking with customers.'
  },
  description: {
    id: 'phone.TextToSpeech.description',
    defaultMessage:
      'Configure the Text to Speech services that will be used by the phone integration. <link>Learn more</link>'
  },
  stepDescription: {
    id: 'phone.TextToSpeech.stepDescription',
    defaultMessage:
      'Select a language, dialect, and voice for use with your assistant.'
  },
  stepDescriptionNote: {
    id: 'phone.TextToSpeech.stepDescriptionNote',
    defaultMessage:
      'Note: these settings will apply globally across your assistant.'
  },
  instanceDescription: {
    id: 'phone.TextToSpeech.instanceDescription',
    defaultMessage:
      'Select an instance to use automatically with your phone integration.'
  },
  voiceModel: {
    id: 'phone.TextToSpeech.voiceModel',
    defaultMessage: 'Choose your Text to Speech voice model'
  },
  voiceSubtitle: {
    id: 'phone.TextToSpeech.voiceSubtitle',
    defaultMessage: 'Text to Speech voice'
  },
  voiceDescription: {
    id: 'phone.TextToSpeech.voiceDescription',
    defaultMessage:
      'Define the language your assistant will use when it transcribes what customers say. <link>Learn more</link>'
  },
  filterVoice: {
    id: 'phone.TextToSpeech.filterVoice',
    defaultMessage: 'Filter voices based on your assistant language'
  },
  filterWarning: {
    id: 'phone.TextToSpeech.filterWarning',
    defaultMessage: 'Turning this off can cause voice mismatching'
  },
  ttsProviderWatson: {
    id: 'phone.TextToSpeech.ttsProviderWatson',
    defaultMessage: 'Watson Text to Speech'
  },
  ttsProviderElevenLabs: {
    id: 'phone.TextToSpeech.ttsProviderElevenLabs',
    defaultMessage: 'ElevenLabs'
  },
  ttsAudioError: {
    id: 'phone.TextToSpeech.ttsAudioError',
    defaultMessage: `Audio failed to load.`
  },
  ttsVoiceLabel: {
    id: 'phone.TextToSpeech.ttsVoiceLabel',
    defaultMessage: 'Choose your Text to Speech voice'
  },
  ttsVoiceDescription: {
    id: 'phone.TextToSpeech.ttsVoiceDescription',
    defaultMessage: 'This is the voice your assistant will speak with'
  },
  ttsVoiceLanguageTitle: {
    id: 'phone.TextToSpeech.ttsVoiceLanguageTitle',
    defaultMessage: 'Language'
  },
  ttsVoicePitchSliderLabel: {
    id: 'phone.TextToSpeech.ttsVoicePitchSliderLabel',
    defaultMessage: 'Adjust Pitch'
  },
  ttsVoiceSpeedSliderLabel: {
    id: 'phone.TextToSpeech.ttsVoiceSpeedSliderLabel',
    defaultMessage: 'Adjust Speed'
  },
  tts11LabsVoiceSpeedSliderLabel: {
    id: 'phone.TextToSpeech.tts11LabsVoiceSpeedSliderLabel',
    defaultMessage: 'Speed'
  },
  tts11LabsVoiceStabilitySliderLabel: {
    id: 'phone.TextToSpeech.tts11LabsVoiceStabilitySliderLabel',
    defaultMessage: 'Stability'
  },
  tts11LabsVoiceStyleSliderLabel: {
    id: 'phone.TextToSpeech.tts11LabsVoiceStyleSliderLabel',
    defaultMessage: 'Style'
  },
  tts11LabsVoiceSimilaritySliderLabel: {
    id: 'phone.TextToSpeech.tts11LabsVoiceSimilaritySliderLabel',
    defaultMessage: 'Similarity'
  },
  tts11LabsVoiceUseSpeakerBoostLabel: {
    id: 'phone.TextToSpeech.tts11LabsVoiceSimilarityliderLabel',
    defaultMessage: 'Use speaker boost'
  },
  ttsVoiceTitle: {
    id: 'phone.TextToSpeech.ttsVoiceTitle',
    defaultMessage: 'Voice'
  },
  ttsInstanceLabel: {
    id: 'phone.TextToSpeech.ttsInstanceLabel',
    defaultMessage: 'Text to Speech instance'
  },
  ttsCustomVoiceLabel: {
    id: 'phone.TextToSpeech.ttsCustomVoiceLabel',
    defaultMessage: 'Custom voice model'
  },
  customModelHelperText: {
    id: 'phone.TextToSpeech.customModelHelperText',
    defaultMessage:
      'This will override the custom voice base model when selected'
  },
  learnMoreLinkTts: {
    id: 'phone.TextToSpeech.learnMoreLinkTts',
    defaultMessage: 'Learn more about Text to Speech'
  },
  createMessage: {
    id: 'phone.TextToSpeech.createMessage',
    defaultMessage:
      'A Text to Speech instance is required to set up a phone integration. By default, the new speech plan will be matched with your {productName} plan. You will not be billed separately for service usage from phone calls. Beyond this integration, any added usage of these speech services will result in separate charges.'
  },
  expressiveModelTooltip: {
    id: 'phone.TextToSpeech.expressiveModelTooltip',
    defaultMessage:
      'Expressive voices have conversational capabilities like expressive styles, emotions, word emphasis, and interjections.'
  },
  expressiveText: {
    id: 'phone.TextToSpeech.expressiveText',
    defaultMessage: 'Expressive'
  },
  moreInformation: {
    id: 'phone.TextToSpeech.moreInformation',
    defaultMessage: 'More information'
  },
  visualizerTextAreaLabel: {
    id: 'phone.TextToSpeech.visualizerTextAreaLabel',
    defaultMessage: 'Use the sample text below or enter your own.'
  },
  visualizerTextAreaPlaceHolder: {
    id: 'phone.TextToSpeech.visualizerTextAreaPlaceHolder',
    defaultMessage: 'Welcome, how can I assist you?'
  },
  playAudioButtonAriaLabel: {
    id: 'phone.TextToSpeech.playAudioButtonAriaLabel',
    defaultMessage: 'Play audio button'
  },
  instanceUrl: {
    id: 'phone.TextToSpeech.instanceUrlL',
    defaultMessage: 'URL instance'
  },
  bearerToken: {
    id: 'phone.TextToSpeech.bearerToken',
    defaultMessage: 'Bearer Token'
  },
  instanceId: {
    id: 'phone.TextToSpeech.instanceIdL',
    defaultMessage: 'Text to Speech instance ID'
  },
  speakerBoost: {
    id: 'phone.TextToSpeech.speakerBoost',
    defaultMessage: 'Use speaker boost'
  },
  customizeVoiceTitleElevenLabs: {
    id: 'phone.TextToSpeech.customizeVoiceTitleElevenLabs',
    defaultMessage: 'Customize voice'
  },
  customizeVoiceDescriptionElevenLabs: {
    id: 'phone.TextToSpeech.customizeVoiceDescriptionElevenLabs',
    defaultMessage: 'Learn more about these settings <link>here</link>'
  },
  configureVoiceTitleElevenLabs: {
    id: 'phone.TextToSpeech.configureVoiceTitleElevenLabs',
    defaultMessage: 'Configure voice'
  },
  configureVoiceDescriptionElevenLabs: {
    id: 'phone.TextToSpeech.configureVoiceDescriptionElevenLabs',
    defaultMessage:
      'Configure the voice your assistant uses when speaking with customers.'
  },
  elevenLabsApiKey: {
    id: 'phone.TextToSpeech.elevenLabsApiKey',
    defaultMessage: 'ElevenLabs API Key'
  },
  apiKey: {
    id: 'phone.TextToSpeech.apiKey',
    defaultMessage: 'API Key'
  },
  model: {
    id: 'phone.TextToSpeech.model',
    defaultMessage: 'Model'
  },
  voice: {
    id: 'phone.TextToSpeech.voice',
    defaultMessage: 'Voice'
  },
  ttsProvider: {
    id: 'phone.TextToSpeech.ttsProvider',
    defaultMessage: 'Text to speech provider'
  },
  selectTTSProvider: {
    id: 'phone.TextToSpeech.selectTTSProvider',
    defaultMessage: 'Select Speech provider'
  }
});
